import { PART_TYPES } from '../ai-assistant.constants';

import type { ChatMessagePart } from '../hooks/use-active-input-part';

export interface MessageWithId {
  id: string;
  [key: string]: unknown;
}

/**
 * Returns a new message array with one message shallow-merged by id.
 * Messages whose id does not match are returned by reference; the matched
 * message is copied and spread with `updates` (immutable update pattern).
 */
export const updateMessageById = <T extends MessageWithId>(
  messages: T[],
  targetId: string,
  updates: Partial<T> & Record<string, unknown>
): T[] =>
  messages.map((message) =>
    message.id !== targetId ? message : { ...message, ...updates }
  );

/** Returns `parts` if non-empty, otherwise wraps fallback `content` in a single MESSAGE part. */
export const resolveMessageParts = (
  source: { parts?: ChatMessagePart[]; content?: string } | null | undefined
): ChatMessagePart[] => {
  if (source?.parts?.length) return source.parts;

  return [{ type: PART_TYPES.MESSAGE, data: { text: source?.content ?? '' } }];
};

/**
 * Returns user-facing AI prompt intro copy based on question count.
 */
export const getPromptMessageText = (questionCount: number): string =>
  questionCount === 1
    ? 'I have a quick question to help me provide a better answer.'
    : `I have ${questionCount} questions to help me provide a better answer.`;

/**
 * A user message is only editable once its reply has fully streamed in —
 * editing targets the reply's own id (see useMarkChat.startEditingMessage),
 * which only exists once the reply is no longer a loading placeholder.
 */
export const isEditableUserMessage = (
  messages: Array<{ isUser?: boolean; loading?: boolean }>,
  index: number
): boolean => {
  const message = messages[index];
  const nextMessage = messages[index + 1];

  return (
    Boolean(message?.isUser) &&
    Boolean(nextMessage) &&
    !nextMessage.isUser &&
    !nextMessage.loading
  );
};
